import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    billingStatus as getBillingStatus,
    BillingStatusData,
    getPaymentInformation,
    GetPaymentInformationData,
    invoicePdf,
    InvoicePdfData,
    listInvoices,
    ListInvoicesData,
    setUsageCeiling,
    SetUsageCeilingData,
    setupPaymentMethod,
    SetupPaymentMethodData,
    syncCheckout,
    SyncCheckoutData,
    updatePaymentInformation,
    UpdatePaymentInformationData,
} from "@/api";
import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";
import { formatKroner, PaceZone, paceZone, UsageMeter, usageMeterState } from "@/app/usageMeterState";
import { Request } from "@/components/CRUD";
import { ActionHandler } from "@/components/UI/tools";
import { Field, Pill, PillTone, Row, Section, TextField } from "./ui";

type ContactFields = {
    street: string;
    postalCode: string;
    city: string;
    country: string;
    cvr: string;
};

const EMPTY_CONTACT: ContactFields = { street: "", postalCode: "", city: "", country: "", cvr: "" };

const contactFromPayment = (payment: any): ContactFields => {
    const address = payment?.billing_address ?? {};
    const text = (value: unknown) => (typeof value === "string" ? value : "");
    return {
        street: text(address.street),
        postalCode: text(address.postal_code),
        city: text(address.city),
        country: text(address.country),
        cvr: text(payment?.billing_cvr),
    };
};

const CONTACT_FIELD_KEYS = Object.keys(EMPTY_CONTACT) as (keyof ContactFields)[];
const OPTIONAL_CONTACT_FIELDS: (keyof ContactFields)[] = ["cvr"];

const BillingContact = observer(({
    orgId,
    canEdit,
    onChanged,
}: {
    orgId: number;
    canEdit: boolean;
    onChanged: () => void;
}) => {
    const [info, setInfo] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState<ContactFields>(EMPTY_CONTACT);
    const [contact, setContact] = useState<ContactFields>(EMPTY_CONTACT);

    const set = (key: keyof ContactFields) => (value: string) =>
        setContact((current) => ({ ...current, [key]: value }));

    const dirty = CONTACT_FIELD_KEYS.some((key) => contact[key].trim() !== saved[key].trim());
    const hasValidContact =
        CONTACT_FIELD_KEYS.every((key) => OPTIONAL_CONTACT_FIELDS.includes(key) || contact[key].trim().length > 0);

    const load = Request<GetPaymentInformationData, any>(
        getPaymentInformation,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data: any) => {
            const payment = data?.payment_information;
            const stored = contactFromPayment(payment);
            setInfo(payment);
            setSaved(stored);
            setContact(stored);
        },
        () => setInfo(null),
        () => orgId > 0,
    );

    const save = Request<UpdatePaymentInformationData, any>(
        updatePaymentInformation,
        () => ({
            body: {
                id: orgId,
                billing_address: {
                    street: contact.street.trim(),
                    postal_code: contact.postalCode.trim(),
                    city: contact.city.trim(),
                    country: contact.country.trim(),
                },
                billing_cvr: contact.cvr.trim() || null,
            },
        }),
        setLoading,
        () => {
            load();
            onChanged();
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("billing.saveContactError"),
            }),
        () => canEdit && dirty && hasValidContact,
    );

    useEffect(() => {
        setSaved(EMPTY_CONTACT);
        setContact(EMPTY_CONTACT);
        load();
    }, [orgId]);

    return (
        <div className="row-form">
            <small className="caption">{appState.t("billing.contact")}</small>

            {info?.invoice_recipient?.email && (
                <p className="detail-text">
                    {appState.t("billing.invoiceRecipient", {
                        name: info.invoice_recipient.name || info.invoice_recipient.email,
                        email: info.invoice_recipient.email,
                    })}
                </p>
            )}

            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("billing.street")}>
                    <TextField value={contact.street} onChange={set("street")} placeholder="—" />
                </Field>
                <Field label={appState.t("billing.postalCode")}>
                    <TextField value={contact.postalCode} onChange={set("postalCode")} placeholder="—" />
                </Field>
            </div>
            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("billing.city")}>
                    <TextField value={contact.city} onChange={set("city")} placeholder="—" />
                </Field>
                <Field label={appState.t("billing.country")}>
                    <TextField value={contact.country} onChange={set("country")} placeholder="—" />
                </Field>
            </div>
            <div className="flex flex-row gap-[.5rem] flex-wrap">
                <Field label={appState.t("billing.cvr")}>
                    <TextField value={contact.cvr} onChange={set("cvr")} placeholder="—" />
                </Field>
            </div>

            <div className="flex flex-row items-center justify-between gap-[.5rem]">
                <p className="error-text">
                    {dirty && !hasValidContact ? appState.t("billing.contactRequired") : ""}
                </p>
                <ActionHandler
                    title={appState.t("common.save")}
                    funq={() => save()}
                    disabled={loading || !dirty || !hasValidContact}
                >
                    {appState.t("common.save")}
                </ActionHandler>
            </div>
        </div>
    );
});

const BILLING_STATUS_KEYS: Record<string, string> = {
    none: "billing.status.none",
    active: "billing.status.active",
    past_due: "billing.status.pastDue",
};

const PACE_TONE: Record<PaceZone, PillTone> = {
    cruise: "good",
    brisk: "warn",
    fast: "danger",
};

/** Accepts "800", "1.500" and "800,50" as typed in Danish. */
const parseKroner = (text: string): number | null => {
    let value = text.replace(/\s|kr\.?/gi, "");
    if (value.includes(",")) value = value.replace(/\./g, "").replace(",", ".");
    else if (/^\d{1,3}(\.\d{3})+$/.test(value)) value = value.replace(/\./g, "");
    const amount = Number(value);
    return value && Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) / 100 : null;
};

type BillingStatus = {
    billing_status: "none" | "active" | "past_due";
    has_payment_method: boolean;
    usage_ceiling: number | null;
    card: { label: string | null; last_four: string | null; expiry_month: number | null; expiry_year: number | null } | null;
    meter: UsageMeter;
};

const cardText = (card: NonNullable<BillingStatus["card"]>) => {
    const name = [card.label, card.last_four ? `•••• ${card.last_four}` : null].filter(Boolean).join(" ");
    const expiry = card.expiry_month && card.expiry_year ? ` · ${String(card.expiry_month).padStart(2, "0")}/${String(card.expiry_year).slice(-2)}` : "";
    return `${name || appState.t("billing.card")}${expiry}`;
};

/**
 * The monthly usage budget: a card that last month's usage is charged to, and
 * the ceiling that stops usage once this month's spend reaches it.
 */
const UsageBudget = observer(({ orgId, canEdit, prominent = false }: { orgId: number; canEdit: boolean; prominent?: boolean }) => {
    const [status, setStatus] = useState<BillingStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [ceilingText, setCeilingText] = useState("");

    const show = (data: BillingStatus) => {
        setStatus(data);
        setCeilingText(data?.usage_ceiling != null ? String(data.usage_ceiling).replace(".", ",") : "");
    };

    const load = Request<BillingStatusData, any>(
        getBillingStatus,
        () => ({ body: { id: orgId } }),
        setLoading,
        show,
        () => setStatus(null),
        () => orgId > 0,
    );

    // Picks up a card checkout whose webhook never landed.
    const sync = Request<SyncCheckoutData, any>(
        syncCheckout,
        () => ({ body: { id: orgId } }),
        setLoading,
        show,
        () => load(),
        () => orgId > 0 && canEdit,
    );

    const setupCard = Request<SetupPaymentMethodData, any>(
        setupPaymentMethod,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data: any) => {
            if (data?.checkout_url) window.location.href = data.checkout_url;
        },
        (error) =>
            appState.setModal({
                title: appState.t("billing.checkoutError"),
                body: error.detail?.toString() ?? appState.t("common.somethingWentWrong"),
            }),
        () => canEdit,
    );

    const ceiling = parseKroner(ceilingText);
    const ceilingDirty = ceiling !== null && ceiling !== status?.usage_ceiling;

    const saveCeiling = Request<SetUsageCeilingData, any>(
        setUsageCeiling,
        () => ({ body: { id: orgId, usage_ceiling: ceiling! } }),
        setLoading,
        () => {
            load();
            void usageMeterState.refresh();
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("billing.ceilingError"),
            }),
        () => canEdit && ceilingDirty,
    );

    useEffect(() => {
        if (canEdit) sync();
        else load();
    }, [orgId, canEdit]);

    const billingStatus = status?.billing_status ?? "none";
    const meter = status?.meter;
    const metered = !!meter && meter.ceiling !== null && !meter.demo_access;
    const spentShare = metered && meter!.ceiling ? Math.min(1, meter!.spent / meter!.ceiling) : 0;

    return (
        <>
            <Row
                trailing={
                    <Pill tone={billingStatus === "active" ? "good" : billingStatus === "past_due" ? "warn" : "neutral"}>
                        {appState.t(BILLING_STATUS_KEYS[billingStatus] ?? BILLING_STATUS_KEYS.none)}
                    </Pill>
                }
            >
                <p className="l">{appState.t("billing.usagePlan")}</p>
                <p className="detail-text">{appState.t("billing.usagePlanHint")}</p>
            </Row>

            {billingStatus === "past_due" && (
                <Row trailing={<Pill tone="warn">{appState.t("billing.status.pastDue")}</Pill>}>
                    <p className="detail-text-strong">{appState.t("billing.pastDueHint")}</p>
                </Row>
            )}

            {metered && (
                <Row trailing={<Pill tone={PACE_TONE[paceZone(meter!.pace)]}>{appState.t(`meter.pace.${paceZone(meter!.pace)}`)}</Pill>}>
                    <p className="l">
                        {appState.t("meter.spentOf", { spent: formatKroner(meter!.spent), ceiling: formatKroner(meter!.ceiling!) })}
                    </p>
                    <span className="usage-gauge-fuel usage-gauge-fuel-large my-[.25rem]">
                        <span
                            className={`usage-gauge-fuel-level ${spentShare >= 1 ? "bg-danger" : spentShare > 0.85 ? "bg-warn" : "bg-good"}`}
                            style={{ width: `${Math.round(spentShare * 100)}%` }}
                        />
                    </span>
                    <p className="detail-text">
                        {appState.t("meter.projection", { amount: formatKroner(meter!.projected_month_end) })}
                        {" · "}
                        {appState.t("meter.daysLeft", { n: Math.max(0, Math.ceil(meter!.days_left)) })}
                    </p>
                </Row>
            )}

            <div className="row-form">
                <div className="flex flex-row items-end gap-[.5rem] flex-wrap">
                    <Field label={appState.t("billing.ceiling")}>
                        <TextField
                            value={ceilingText}
                            onChange={setCeilingText}
                            placeholder={appState.t("billing.ceilingPlaceholder")}
                            readOnly={!canEdit}
                        />
                    </Field>
                    {canEdit && (
                        <ActionHandler
                            title={appState.t("common.save")}
                            funq={() => saveCeiling()}
                            disabled={loading || !ceilingDirty}
                        >
                            {appState.t("common.save")}
                        </ActionHandler>
                    )}
                </div>
                <p className={ceilingText && ceiling === null ? "error-text" : "detail-text"}>
                    {ceilingText && ceiling === null
                        ? appState.t("billing.ceilingInvalid")
                        : status?.usage_ceiling == null
                            ? appState.t("billing.ceilingRequired")
                            : appState.t("billing.ceilingHint")}
                </p>
            </div>

            <Row
                trailing={
                    canEdit ? (
                        <ActionHandler
                            title={appState.t(status?.has_payment_method ? "billing.changeCard" : "billing.addCard")}
                            funq={() => setupCard()}
                            disabled={loading}
                            glow={prominent && !status?.has_payment_method}
                        >
                            {appState.t(status?.has_payment_method ? "billing.changeCard" : "billing.addCard")}
                        </ActionHandler>
                    ) : undefined
                }
            >
                <p className="l">{appState.t("billing.card")}</p>
                <p className="detail-text">
                    {status?.card ? cardText(status.card) : appState.t("billing.noCard")}
                </p>
            </Row>
        </>
    );
});

type InvoiceSummary = {
    id: number;
    invoice_number: string;
    kind: "invoice" | "credit_note";
    issued_at: string;
    total: string;
    currency: string;
    status: "issued" | "credited";
};

const Invoices = observer(({ orgId }: { orgId: number }) => {
    const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
    const [loading, setLoading] = useState(false);

    const load = Request<ListInvoicesData, any>(
        listInvoices,
        () => ({ body: { id: orgId } }),
        setLoading,
        (data: any) => setInvoices(data?.invoices ?? []),
        () => setInvoices([]),
        () => orgId > 0,
    );

    const download = Request<InvoicePdfData, any, InvoiceSummary>(
        invoicePdf,
        (invoice) => ({ body: { id: orgId, invoice_id: invoice.id } }),
        setLoading,
        (pdf: Blob, invoice) => {
            const url = URL.createObjectURL(pdf);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${invoice.invoice_number}.pdf`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        () =>
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("billing.invoice.downloadError"),
            }),
        () => !loading,
    );

    useEffect(() => {
        setInvoices([]);
        load();
    }, [orgId]);

    if (invoices.length === 0) return null;

    return (
        <div className="flex flex-col py-[.55rem]">
            <small className="caption px-1 pb-[.25rem]">{appState.t("billing.invoices")}</small>
            {invoices.map((invoice) => (
                <Row
                    key={invoice.id}
                    onClick={() => download(invoice)}
                    disabled={loading}
                    trailing={
                        <div className="flex flex-row items-center gap-[.35rem]">
                            {invoice.kind === "credit_note" && <Pill tone="warn">{appState.t("billing.invoice.creditNote")}</Pill>}
                            <p className="detail-text-strong whitespace-nowrap">
                                {`${Number(invoice.total).toLocaleString(appState.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${invoice.currency}`}
                            </p>
                        </div>
                    }
                >
                    <p className="l">{invoice.invoice_number}</p>
                    <p className="detail-text">
                        {`${new Date(invoice.issued_at).toLocaleDateString(appState.locale)} · ${appState.t("billing.invoice.download")}`}
                    </p>
                </Row>
            ))}
        </div>
    );
});

/**
 * Billing has three honest states: this organisation holds the payment, it is
 * covered by an ancestor, or nothing on this path pays yet. A missing payment
 * record on a child is not the same as being unbilled, so we never draw a local
 * payment editor for an inherited plan.
 */
export const BillingSection = observer(({
    node,
    holder,
    canEdit,
    onSelectOrg,
    onChanged,
}: {
    node: organisation_tree_node;
    holder?: organisation_tree_node;
    canEdit: boolean;
    onSelectOrg: (id: number) => void;
    onChanged: () => void;
}) => {
    if (node.has_payment) {
        return (
            <Section title={appState.t("billing.title")} hint={appState.t("billing.managedHere")}>
                <UsageBudget orgId={node.id} canEdit={canEdit} />
                {canEdit && <BillingContact orgId={node.id} canEdit onChanged={onChanged} />}
                {canEdit && <Invoices orgId={node.id} />}
            </Section>
        );
    }

    if (node.billing_organisation_id) {
        return (
            <Section title={appState.t("billing.title")}>
                <Row
                    onClick={holder ? () => onSelectOrg(holder.id) : undefined}
                    chevron={!!holder}
                    trailing={<Pill tone="neutral">{appState.t("common.inherited")}</Pill>}
                >
                    <p className="l">
                        {appState.t("billing.coveredBy", { name: holder?.name ?? `${node.billing_organisation_id}` })}
                    </p>
                    <p className="detail-text">
                        {holder
                            ? appState.t("billing.coveredByHint")
                            : appState.t("billing.coveredByUnavailable")}
                    </p>
                </Row>
            </Section>
        );
    }

    return (
        <Section title={appState.t("billing.title")}>
            {canEdit ? (
                <>
                    <UsageBudget orgId={node.id} canEdit prominent />
                    <BillingContact orgId={node.id} canEdit={canEdit} onChanged={onChanged} />
                </>
            ) : (
                <Row trailing={<Pill tone="warn">{appState.t("billing.unpaid")}</Pill>}>
                    <p className="l">{appState.t("billing.noPaymentPath")}</p>
                    <p className="detail-text">
                        {appState.t("billing.registerHint")}
                    </p>
                </Row>
            )}
        </Section>
    );
});
